"use strict";

import React from "react";
import { IndividualRepMarker } from "./rep_graphics.js";
import { SCRIBBLE_COLORS } from "./crayon.js";
import {
  linearTransform,
  simpleTitleCase,
  topSorted,
  scoreIsMissing,
} from "./shared.js";
import { RawScore, ChangeDisplay, WrappedText } from "./components.js";
import { PRODUCT_KEYS as S } from "./data_helpers.js";

const GRAPH_AXIS_POS_SINGLE = "single";
const GRAPH_AXIS_POS_LEFT = "left";
const GRAPH_AXIS_POS_RIGHT = "right";
const GRAPH_AXIS_POS_INTERIOR = "interior";

let GraphAxis = ({
  rankRanges,
  yearLabel,
  result = null,
  x,
  yScale,
  position,
  className,
  narrowStyle,
}) => {
  let W = 10;
  let LP = result !== null ? 25 : 15;
  let labeledScores = [0, ...rankRanges.map(({ range: [_, r1] }) => r1)];
  let scoreLabels =
    position === GRAPH_AXIS_POS_SINGLE || position === GRAPH_AXIS_POS_RIGHT;
  let rankLabels =
    position === GRAPH_AXIS_POS_SINGLE || position === GRAPH_AXIS_POS_LEFT;

  // ~HACK: since BEL has blank rank labels, we want to duplicate *score* labels on the left
  let belLabels = false;
  if (rankLabels && rankRanges.length && !rankRanges[0].rank.label) {
    rankLabels = false;
    belLabels = position !== GRAPH_AXIS_POS_SINGLE;
  }

  return (
    <g className={`axis ${className}`}>
      {yearLabel !== null ? (
        <WrappedText
          className="yearLabel"
          x={x}
          y={yScale(100) - LP}
          textAnchor="middle"
          config={{ MAX_CHARS: narrowStyle ? 8 : 45, ALIGN: "bottom" }}
        >
          {yearLabel}
        </WrappedText>
      ) : null}
      {result !== null ? (
        <text
          className="resultLabel"
          textAnchor="middle"
          x={x}
          y={yScale(100) - 10}
        >
          <RawScore result={result} Wrapper="tspan" />
        </text>
      ) : null}
      {labeledScores.map((s) => {
        let y = yScale(s);
        return (
          <g key={s} className="scoreLabel">
            <line
              x1={x - (position === GRAPH_AXIS_POS_RIGHT ? 0 : W / 2)}
              y1={y}
              x2={x + (position === GRAPH_AXIS_POS_LEFT ? 0 : W / 2)}
              y2={y}
            />
            {scoreLabels ? (
              <text
                y={y}
                x={x + W / 2 + 5}
                textAnchor="start"
                alignmentBaseline="middle"
              >
                {s}
              </text>
            ) : null}
            {belLabels ? (
              <text
                y={y}
                x={x - W / 2 - 5}
                textAnchor="end"
                alignmentBaseline="middle"
              >
                {s}
              </text>
            ) : null}
          </g>
        );
      })}
      {rankRanges.map(({ rank, range: [r0, r1] }) => (
        <g key={r0} className="rankRange">
          <line
            x1={x}
            y1={yScale(r0)}
            x2={x}
            y2={yScale(r1)}
            className={`rank-${rank.code}-line`}
          />
          {rankLabels ? (
            <WrappedText
              x={x - 20}
              y={yScale((r0 + r1) / 2)}
              textAnchor="end"
              config={{ MAX_CHARS: 15, ALIGN: "center" }}
            >
              {simpleTitleCase(rank.label)}
            </WrappedText>
          ) : null}
        </g>
      ))}
    </g>
  );
};

let BasicMarker = ({
  survey,
  item,
  result,
  resultIdx,
  primary,
  selected,
  color,
  x,
  yScale,
  onClick,
}) => {
  let R = primary ? 6.5 : 5;
  let Ri = R / 2;
  let Ro = survey.product_key === S.ECE ? R * 1.65 : R * 2;

  if (scoreIsMissing(result)) return null;

  let y = yScale(result.score);
  return (
    <g
      className={`marker rank-${result.code} ${selected ? "selected" : ""}`}
      style={
        color
          ? {
              // for ECE, override rank- coloring
              color,
              fill: color,
            }
          : null
      }
      data-path={`${item._level}/${item.slug}`}
      data-result-idx={resultIdx}
      onClick={onClick}
    >
      <circle className="outerRing" cx={x} cy={y} r={Ro} />
      <circle className="mainDot" cx={x} cy={y} r={R} />
      <circle className="innerDot" cx={x} cy={y} r={Ri} />
    </g>
  );
};

let LegendLabelGroup = ({
  survey,
  item,
  result,
  transform,
  generateLink,
  showChange = true,
}) => {
  let S = 15;
  let P = 15;
  let W = 250;

  return (
    <a
      className="label-group"
      transform={transform}
      xlinkHref={generateLink ? generateLink(item) : null}
    >
      <IndividualRepMarker
        survey={survey}
        item={item}
        result={result}
        x={0}
        yScale={(_) => 0}
        alwaysDisplay={true}
      />
      <text x={S + P}>{item.name}</text>
      <text x={W} textAnchor="end">
        <RawScore result={result} Wrapper="tspan" />
        {showChange ? (
          <tspan>
            {" "}
            (<ChangeDisplay result={result} Wrapper="tspan" />)
          </tspan>
        ) : null}
      </text>
    </a>
  );
};

let LegendLabelLines = ({ sortedItems, x1, yScale1, x2, yScale2 }) => {
  let xMid = (x1 + x2) / 2;
  return (
    <g className="label-lines">
      {sortedItems.map((d, i) => {
        let result = d._results.latest;
        if (scoreIsMissing(result)) return null;
        let y1 = yScale1(result.score);
        return (
          <polyline
            key={d.slug}
            points={`${x1},${y1} ${xMid},${y1} ${x2},${yScale2(i + 0.5)}`}
          />
        );
      })}
    </g>
  );
};

let GraphTrendLine = ({
  gradientId,
  item,
  results: [resultA, resultB],
  primary,
  selected,
  x1,
  x2,
  yScale,
  onClick,
}) => {
  if (scoreIsMissing(resultA) || scoreIsMissing(resultB)) return null;

  let lineEnds = {
    x1,
    x2,
    y1: yScale(resultA.score),
    y2: yScale(resultB.score),
  };
  return (
    <g
      data-path={`${item._level}/${item.slug}`}
      className={`trend ${primary ? "primary" : ""} ${
        selected ? "selected" : ""
      } ${resultB.newref ? "discontinuous" : ""}`}
    >
      <linearGradient
        gradientUnits="userSpaceOnUse"
        className={`rank-blend start-rank-${resultA.rank.code} final-rank-${resultB.rank.code}`}
        id={gradientId}
        {...lineEnds}
      >
        <stop offset="0" />
        <stop offset="1" />
      </linearGradient>
      <line stroke={`url(#${gradientId})`} onClick={onClick} {...lineEnds} />
    </g>
  );
};

export function Graph({
  rankRanges,
  survey,
  activeView,
  narrowStyle = false,
  objects,
  activeObject,
  selectedObject,
  selectObject,
  showResult = false,
  markerStyle = null,
  showLegend = false,
  generateLink = null,
  config: { W = null, H = 600, YW = 165, P = 50 } = {},
  graphId = "main",
}) {
  let DotMarker =
    markerStyle === "individual-rep" ? IndividualRepMarker : BasicMarker;
  let allDotsPrimary = markerStyle === "selected-primary";
  let moreColorfulDots =
    markerStyle === "more-colorful" || markerStyle === "selected-primary";

  let resultIndexes, groupOffsets, yearIndexes;
  const _nprior = survey._fullYears.length;
  switch (activeView) {
    case "performance":
      resultIndexes = [_nprior - 1]; // same as `['latest']`
      break;
    case "change":
      resultIndexes = _nprior < 2 ? [_nprior - 1] : [_nprior - 2, _nprior - 1];
      break;
    case "trends":
      resultIndexes = [...Array(_nprior).keys()];
      break;
    case "trends-3":
      resultIndexes = [...Array(_nprior).keys()].slice(-3);
      break;
    case "trends-bel":
      resultIndexes = [];
      groupOffsets = [];
      yearIndexes = [];
      survey._schoolYears.forEach((yearGroup) => {
        let xIdx, getIdx;
        const _baseIdx = resultIndexes.length;
        if (yearGroup.length === 2) {
          resultIndexes.push(
            yearGroup[0]._result_index,
            null, // ~HACK: achieve desired visual spacing
            yearGroup[1]._result_index,
          );
          xIdx = resultIndexes.length - 3 / 2;
          getIdx = (idx) => _baseIdx + 2 * idx;
        } else {
          resultIndexes.push(...yearGroup.map((sy) => sy._result_index));
          xIdx = resultIndexes.length - yearGroup.length / 2;
          getIdx = (idx) => _baseIdx + idx;
        }
        yearIndexes.push(yearGroup[0]._result_index);
        groupOffsets.push({ yearGroup, xIdx, getIdx });
      });
      break;
  }

  if (W === null) W = YW * Math.max(2, resultIndexes.length);
  if (narrowStyle) {
    W -= YW; // tighten up horizontal space regardless of how many years shown
  }
  let xScale = linearTransform([0, resultIndexes.length], [0 + P, W - P]);
  let yScale = linearTransform([100, 0], [0 + Math.max(35, P), H - P]);
  function forEachYear(object, fn) {
    let [prev_i, prevResult] = [null, null];
    return resultIndexes.map((idx, i) => {
      let result;

      // special handling for new BEL split-year graphs
      if (idx === null) {
        return null;
      } else if (yearIndexes?.includes(idx)) {
        [prev_i, prevResult] = [null, null];
      }

      if (object) {
        result = object._results[idx];
        if (
          object._alias &&
          object._children.some((d) => !scoreIsMissing(d._results[idx]))
        ) {
          result.score = true; // HACK: prevent fading years axis when a child has data
        }
      } else {
        // ~HACK: fallback needed for year labels when no `selectedObject`
        result = { survey: survey._fullYears[idx] };
        // ~HACK: …and in this case, base axis `no-data` on all objects displayed
        result.score =
          objects.some((d) => !scoreIsMissing(d._results[idx])) || null;
      }
      let el = fn({ idx, result, prevResult }, i, prev_i);
      if (!scoreIsMissing(result)) {
        [prev_i, prevResult] = [i, result];
      }
      return el;
    });
  }
  function axisPosition(i) {
    //if (resultIndexes[i] === 'latest') return GRAPH_AXIS_POS_SINGLE
    if (narrowStyle && narrowStyle !== "except-labels")
      return GRAPH_AXIS_POS_INTERIOR;
    else if (activeView === "performance") return GRAPH_AXIS_POS_SINGLE;
    else if (i === 0) return GRAPH_AXIS_POS_LEFT;
    else if (i === resultIndexes.length - 1) return GRAPH_AXIS_POS_RIGHT;
    else return GRAPH_AXIS_POS_INTERIOR;
  }
  let optionalLines = null;
  let optionalLegend = null;
  let optionalLabels = null;
  if (showLegend) {
    let LH = 250;
    let LW = 250;
    let LX = W;
    W += LW;
    let PX = 15;
    let PY = (H - LH) / 2;

    let sortedItems = topSorted(objects, "-score", null);
    let yScaleL = linearTransform([0, sortedItems.length], [PY, PY + LH]);
    optionalLines = (
      <LegendLabelLines
        sortedItems={sortedItems}
        x1={xScale(resultIndexes.length - 0.5)}
        yScale1={yScale}
        x2={LX - PX}
        yScale2={yScaleL}
      />
    );
    optionalLegend = (
      <g className="legend">
        {sortedItems.map((d, i) => (
          <LegendLabelGroup
            key={d.slug}
            survey={survey}
            item={d}
            result={d._results.latest}
            transform={`translate(${LX}, ${yScaleL(i + 0.5)})`}
            generateLink={generateLink}
            showChange={resultIndexes.length > 1}
          />
        ))}
      </g>
    );
  }
  if (groupOffsets) {
    function locatePopup(yearGroup, object) {
      let prevResult, prevIndex;
      let midIdx = yearGroup.length / 2;
      for (let [idx, sy] of yearGroup.entries()) {
        let currResult = object._results[sy._result_index];
        if (scoreIsMissing(currResult)) {
          continue;
        } else if (idx < midIdx) {
          prevResult = currResult;
          prevIndex = idx;
          continue;
        }
        if (prevResult && idx == midIdx) {
          // ~HACK: fake a "result" with average of both sides
          prevResult = {
            score: (prevResult.score + currResult.score) / 2,
          };
          prevIndex = idx - 0.5;
        } else if (!prevResult) {
          prevResult = currResult;
          prevIndex = idx;
        }
        break;
      }
      return prevResult
        ? {
            score: prevResult.score,
            index: prevIndex,
          }
        : null;
    }

    optionalLabels = (
      <g className="xtraSchoolYearLabels">
        {survey._schoolYears.map((yearGroup, idx) => {
          let x = xScale(groupOffsets[idx].xIdx);
          let pi = selectedObject && locatePopup(yearGroup, selectedObject);
          const PIXEL_FUDGE = -2; // HACK: for whatever reason the anchors weren't on-axis
          return (
            <g key={idx}>
              <text
                className={yearGroup._reverseIdx !== 0 ? "prior" : null}
                textAnchor="middle"
                x={x}
                y={yScale(-5)}
              >
                {yearGroup._label}
              </text>
              {pi && (
                <circle
                  className="selected-object-popup-anchor"
                  data-syIdx={idx}
                  cx={
                    xScale(groupOffsets[idx].getIdx(pi.index) + 0.5) +
                    PIXEL_FUDGE
                  }
                  cy={yScale(pi.score)}
                  r={15}
                />
              )}
            </g>
          );
        })}
      </g>
    );
  }
  let hasSelectedItem = objects.some((d) => d === selectedObject);
  return (
    <svg
      viewBox={[0, 0, W, H].join(" ")}
      width={W}
      height={H}
      className={`results-graph ${hasSelectedItem ? "has-selected-item" : ""}`}
    >
      {0 ? (
        <rect className="debug" fill="red" x={0} y={0} width={W} height={H} />
      ) : null}
      {forEachYear(selectedObject, ({ idx, result }, i) => (
        <GraphAxis
          key={idx}
          className={scoreIsMissing(result) ? "no-data" : ""}
          rankRanges={rankRanges}
          yearLabel={result.survey.label}
          result={showResult ? result : null}
          x={xScale(i + 0.5)}
          yScale={yScale}
          position={axisPosition(i)}
          narrowStyle={narrowStyle}
        />
      ))}
      {optionalLines}
      {optionalLegend}
      {optionalLabels}
      {objects.map((d) => {
        let selected = d === selectedObject;
        let primary = d === activeObject;
        let color = null;
        if (
          survey.themeSlugs &&
          d._level === "essentials" &&
          (primary || selected || moreColorfulDots)
        ) {
          let colKey = survey.themeSlugs[d.slug];
          color = SCRIBBLE_COLORS[colKey];
        }
        return (
          <g
            key={d.slug}
            className={`whole-item ${primary ? "primary" : ""} ${
              selected ? "selected" : ""
            }`}
          >
            {[
              ...forEachYear(d, ({ idx, result, prevResult }, i, prev_i) =>
                prevResult ? (
                  <GraphTrendLine
                    key={`${idx}-${d.slug}-trend`}
                    gradientId={`gradient-trend-${d.slug}-${idx}-${graphId}`}
                    item={d}
                    results={[prevResult, result]}
                    primary={primary}
                    selected={selected}
                    x1={xScale(prev_i + 0.5)}
                    x2={xScale(i + 0.5)}
                    yScale={yScale}
                    onClick={() => selectObject(d._level, d.slug)}
                  />
                ) : null,
              ),
              ...forEachYear(d, ({ idx, result }, i) => (
                <DotMarker
                  key={`${idx}-${d.slug}-mark`}
                  survey={survey}
                  item={d}
                  result={result}
                  resultIdx={idx}
                  primary={primary || allDotsPrimary}
                  selected={selected || allDotsPrimary}
                  color={color}
                  x={xScale(i + 0.5)}
                  yScale={yScale}
                  onClick={() => selectObject(d._level, d.slug)}
                />
              )),
            ]}
          </g>
        );
      })}
    </svg>
  );
}

export function ThermometerGraph({
  rankRanges,
  survey,
  objects,
  activeObject,
  selectedObject,
  selectObject,
  config: { W = 50, H = 1000, P = 5 } = {},
}) {
  let yScale = linearTransform([100, 0], [0 + P, H - P]);
  return (
    <svg
      className="thermometer-graph"
      viewBox={[0, 0, W, H].join(" ")}
      width={W}
      height={H}
    >
      {0 ? <rect x={0} y={0} width={W} height={H} fill="pink" /> : null}
      <GraphAxis
        className="thermometer"
        rankRanges={rankRanges}
        yearLabel={null}
        x={W / 2}
        yScale={yScale}
        position={GRAPH_AXIS_POS_INTERIOR}
      />
      {objects.map((d) => {
        // NOTE: this is copy-pasta from <Graph />
        let selected = d === selectedObject;
        let primary = d === activeObject;
        let color = null;
        if (survey.themeSlugs && d._level === "essentials") {
          let colKey = survey.themeSlugs[d.slug];
          color = SCRIBBLE_COLORS[colKey];
        }
        return (
          <BasicMarker
            key={d.slug}
            survey={survey}
            item={d}
            result={d._results.latest}
            resultIdx={-1}
            primary={primary}
            selected={selected}
            color={color}
            x={W / 2}
            yScale={yScale}
            onClick={() => selectObject(d._level, d.slug)}
          />
        );
      })}
    </svg>
  );
}
